/** Yatishara Studio logo paths. Export names stay stable for older Desk components. */

const BASE = "/branding";

/**
 * Transparent π rasters: `sanity-v2-logo-light-*` (white) and `sanity-v2-logo-dark-*` (black).
 * `sanity-v2-logo-dark-on-white-*` is a π on an opaque white plate — OG/email only.
 * Sidebar/header/tab chrome pick ink via appearance. Boot/BrandMark still use white + invert.
 */
/** Default sidebar mark (dark canvas). Light chrome uses mercuryLogoSidebarSrc("light"). */
export const MERCURY_LOGO_SIDEBAR = `${BASE}/sanity-v2-logo-light-96.webp`;
/**
 * Transparent white π; boot/update overlays invert it on light plates.
 *
 * Not a `rel=preload` list any more. React 19 already emits a srcset-aware
 * preload for the boot <img> and places it ahead of the stylesheet; a manual
 * entry in <head> cannot beat that ordering (React reorders <head> children
 * into the metadata section) and only added a second, size-blind reference to
 * the same file. Prioritise the image at its source instead — fetchPriority on
 * the img in logo-loader.tsx.
 */
export const MERCURY_LOGO_BOOT = `${BASE}/sanity-v2-logo-light-192.webp`;

export type LogoInk = "light" | "dark";
export type AppearanceMode = "light" | "dark";

type LogoFormat = "webp" | "png";

/** Dark ink on light canvas; light ink on dark canvas. */
export function logoInkForAppearance(appearance?: string | null): LogoInk {
  return appearance === "light" ? "dark" : "light";
}

function logoPath(px: number, format: LogoFormat, ink: LogoInk = "light") {
  const bucket = px <= 32 ? 96 : px;
  const stem = ink === "dark" ? "sanity-v2-logo-dark" : "sanity-v2-logo-light";
  return `${BASE}/${stem}-${bucket}.${format}`;
}

export function mercuryLogoSidebarSrc(appearance?: string | null) {
  return logoPath(96, "webp", logoInkForAppearance(appearance));
}

/** Pick raster bucket + srcSet for a CSS display size. Always the transparent white π. */
export function mercuryLogoAssets(cssPx: number, _appearance?: string | null) {
  const base = cssPx <= 80 ? 96 : cssPx <= 160 ? 192 : 384;
  const retinaByBase: Record<number, number> = { 32: 96, 96: 192, 192: 384, 384: 512 };
  const retina = retinaByBase[base] ?? base;
  const webp1 = logoPath(base, "webp", "light");
  const webp2 = logoPath(retina, "webp", "light");
  return {
    src: webp2,
    srcSet: `${webp1} ${base}w, ${webp2} ${retina}w`,
    sizes: `${Math.round(cssPx)}px`,
    fallback: logoPath(base, "png", "light"),
    fallback2x: logoPath(retina, "png", "light"),
  };
}
